import { throttle } from "lodash-es";
import { useEffect, useMemo, useRef } from "react";

interface UseInfiniteScrollOptions {
  /**
   * 容器引用，默认为 window
   */
  containerRef?: React.RefObject<HTMLElement>;
  /**
   * 距离底部的阈值，默认 20px
   */
  threshold?: number;
  /**
   * 节流延迟时间，默认 200ms
   */
  throttleDelay?: number;
  /**
   * 是否禁用无限滚动
   */
  disabled?: boolean;
  /**
   * 加载更多数据的回调函数
   */
  onLoadMore: () => Promise<unknown> | undefined;
  /**
   * 下一页按钮的选择器，如果提供则优先检查按钮可见性
   */
  nextButtonSelector?: string;
}

/**
 * 无限滚动 Hook
 * @description 监听滚动事件，当接近底部时触发加载更多数据
 */
export function useInfiniteScroll({
  containerRef,
  threshold = 20,
  throttleDelay = 200,
  disabled = false,
  onLoadMore,
  nextButtonSelector,
}: UseInfiniteScrollOptions) {
  const onLoadMoreRef = useRef(onLoadMore);

  // 更新回调函数引用
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        const container = containerRef?.current || window;
        let scrollTop: number;
        let scrollHeight: number;
        let clientHeight: number;

        if (container === window) {
          scrollTop = window.scrollY || document.documentElement.scrollTop;
          scrollHeight = document.documentElement.scrollHeight;
          clientHeight = window.innerHeight;
        } else {
          const el = container as HTMLElement;
          scrollTop = el.scrollTop;
          scrollHeight = el.scrollHeight;
          clientHeight = el.clientHeight;
        }

        // 优先检查下一页按钮是否可见
        if (nextButtonSelector) {
          const nextButton = document.querySelector(nextButtonSelector);
          if (nextButton) {
            const rect = nextButton.getBoundingClientRect();
            const isVisible = rect.top <= clientHeight && rect.bottom >= 0;
            if (isVisible) {
              void onLoadMoreRef.current();
              return;
            }
          }
        }

        // 检查是否接近底部
        if (clientHeight + scrollTop >= scrollHeight - threshold) {
          void onLoadMoreRef.current();
        }
      }, throttleDelay) as EventListener,
    [containerRef, threshold, throttleDelay, nextButtonSelector],
  );

  useEffect(() => {
    if (disabled) return;

    const target = containerRef?.current || window;
    target.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      target.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, handleScroll, disabled]);

  return {
    handleScroll,
  };
}
